import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import AdminNavbar from "../Layout/AdminNavbar";
import API_BASE_URL from "../config";

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100, 500];

const fmt = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: true,
  });
};

const SORT_FIELDS = {
  id:         "id",
  entityType: "entityType",
  entityId:   "entityId",
  entityName: "entityName",
  deletedBy:  "deletedBy",
  deletedAt:  "deletedAt",
};

export default function AllDeletedEntries() {
  const navigate = useNavigate();

  // ── data ──────────────────────────────────────────────
  const [entries, setEntries]       = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [entityTypes, setEntityTypes] = useState([]);
  const [loading, setLoading]       = useState(false);

  // ── filters ───────────────────────────────────────────
  const [search, setSearch]         = useState("");
  const [entityTypeFilter, setEntityTypeFilter] = useState("");
  const [page, setPage]             = useState(0);
  const [pageSize, setPageSize]     = useState(20);
  const [sortField, setSortField]   = useState("deletedAt");
  const [sortDir, setSortDir]       = useState("desc");
  const [showFilters, setShowFilters] = useState(true);
  const [selected, setSelected]       = useState(new Set());
  const [deleting, setDeleting]       = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const token = localStorage.getItem("token");

  // ── fetch entity types for dropdown ───────────────────
  useEffect(() => {
    if (!token) return;
    fetch(`${API_BASE_URL}/api/delete-audit-logs/entity-types`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => setEntityTypes(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [token]);

  // ── fetch entries ──────────────────────────────────────
  const fetchEntries = useCallback(() => {
    if (!token) { navigate("/"); return; }
    setLoading(true);
    const params = new URLSearchParams({
      page, size: pageSize,
      sortField, sortDir,
      ...(search         && { search }),
      ...(entityTypeFilter && { entityType: entityTypeFilter }),
    });
    fetch(`${API_BASE_URL}/api/delete-audit-logs?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then((data) => {
        setEntries(data.content || []);
        setTotalPages(data.totalPages || 0);
        setTotalItems(data.totalElements || 0);
      })
      .catch((err) => {
        if (err.message === "401" || err.message === "403") { navigate("/"); return; }
        toast.error("Failed to load deleted entries.");
      })
      .finally(() => setLoading(false));
  }, [token, page, pageSize, sortField, sortDir, search, entityTypeFilter, navigate]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);
  useEffect(() => { setSelected(new Set()); }, [page, pageSize]);

  // ── bulk delete ────────────────────────────────────────
  const handleBulkDelete = () => {
    if (selected.size === 0) return;
    setShowConfirm(true);
  };

  const confirmDelete = () => {
    setShowConfirm(false);
    setDeleting(true);
    fetch(`${API_BASE_URL}/api/delete-audit-logs/bulk`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify([...selected]),
    })
      .then((r) => { if (!r.ok) throw new Error(r.status); })
      .then(() => { toast.success(`${selected.size} entr${selected.size === 1 ? "y" : "ies"} deleted.`); setSelected(new Set()); fetchEntries(); })
      .catch(() => toast.error("Failed to delete selected entries."))
      .finally(() => setDeleting(false));
  };

  // ── selection helpers ──────────────────────────────────
  const allOnPageSelected = entries.length > 0 && entries.every((e) => selected.has(e.id));
  const someOnPageSelected = entries.some((e) => selected.has(e.id));
  const toggleRow = (id) => setSelected((prev) => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  const toggleAll = () => {
    if (allOnPageSelected) {
      setSelected((prev) => { const s = new Set(prev); entries.forEach((e) => s.delete(e.id)); return s; });
    } else {
      setSelected((prev) => { const s = new Set(prev); entries.forEach((e) => s.add(e.id)); return s; });
    }
  };

  // ── sort toggle ────────────────────────────────────────
  const handleSort = (field) => {
    if (sortField === SORT_FIELDS[field]) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(SORT_FIELDS[field]);
      setSortDir("desc");
    }
    setPage(0);
  };

  const SortArrow = ({ field }) => {
    if (sortField !== SORT_FIELDS[field]) return <span style={{ opacity: 0.3 }}> ↕</span>;
    return <span style={{ color: "#5b5fc7" }}>{sortDir === "asc" ? " ↑" : " ↓"}</span>;
  };

  const handleReset = () => {
    setSearch("");
    setEntityTypeFilter("");
    setPage(0);
    setSortField("deletedAt");
    setSortDir("desc");
  };

  // ── entity type badge colors ───────────────────────────
  const typeColor = (type) => {
    const map = {
      Employee: "#0d6efd", Customer: "#6f42c1", Asset: "#fd7e14",
      Project: "#198754", Contact: "#20c997", Department: "#0dcaf0",
      Designation: "#6610f2", Vendor: "#dc3545", Task: "#ffc107",
      Sprint: "#d63384", Holiday: "#0dcaf0", "Leave Type": "#17a2b8",
      Announcement: "#6c757d",
    };
    return map[type] || "#6c757d";
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f5f6fa" }}>
      <div style={{ position: "sticky", top: 0, zIndex: 1100 }}>
        <AdminNavbar />
      </div>

      <div style={{ padding: "12px 32px" }}>

        {/* ── Header ── */}
        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
          <div>
            <h4 className="mb-0 fw-bold" style={{ color: "#080235" }}>
              <i className="bi bi-trash3 me-2" style={{ color: "#dc3545" }} />
              Removed Records History
            </h4>
            <p className="text-muted mb-0" style={{ fontSize: 13 }}>
              Audit trail of all records deleted across the system
            </p>
          </div>
          <div className="d-flex gap-2">
            <button
              className="btn btn-sm btn-outline-secondary"
              style={{ borderRadius: 8, fontSize: 13 }}
              onClick={() => setShowFilters((v) => !v)}
            >
              <i className={`bi bi-funnel${showFilters ? "-fill" : ""} me-1`} />
              Filters
            </button>
            <button
              className="btn btn-sm"
              style={{ borderRadius: 8, fontSize: 13, background: "#080235", color: "#fff" }}
              onClick={fetchEntries}
            >
              <i className="bi bi-arrow-clockwise me-1" />
              Refresh
            </button>
          </div>
        </div>

        {/* ── Filter Panel ── */}
        {showFilters && (
          <div
            className="card border-0 mb-3"
            style={{ borderRadius: 12, boxShadow: "0 2px 12px rgba(8,2,53,0.07)" }}
          >
            <div className="card-body py-3">
              <div className="row g-3 align-items-end">
                <div className="col-12 col-md-3">
                  <label className="form-label fw-semibold" style={{ fontSize: 12, color: "#6b7394" }}>
                    Search
                  </label>
                  <input
                    className="form-control"
                    style={{ borderRadius: 8, fontSize: 13 }}
                    placeholder="Search by type, name, deleted by…"
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                  />
                </div>
                <div className="col-12 col-md-2">
                  <label className="form-label fw-semibold" style={{ fontSize: 12, color: "#6b7394" }}>
                    Module / Entity Type
                  </label>
                  <select
                    className="form-select"
                    style={{ borderRadius: 8, fontSize: 13 }}
                    value={entityTypeFilter}
                    onChange={(e) => { setEntityTypeFilter(e.target.value); setPage(0); }}
                  >
                    <option value="">All Modules</option>
                    {entityTypes.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div className="col-12 col-md-1">
                  <label className="form-label fw-semibold" style={{ fontSize: 12, color: "#6b7394" }}>
                    Rows per page
                  </label>
                  <select
                    className="form-select"
                    style={{ borderRadius: 8, fontSize: 13 }}
                    value={pageSize}
                    onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
                  >
                    {PAGE_SIZE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
                <div className="col-12 col-md-1">
                  <button
                    className="btn btn-sm btn-primary w-90"
                    style={{ borderRadius: 8, fontSize: 13 }}
                    onClick={handleReset}
                  >
                    <i className="bi bi-x-circle me-1" />
                    Reset
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Stats bar ── */}
        <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
          <span style={{ fontSize: 13, color: "#6b7394" }}>
            {loading ? "Loading…" : `${totalItems.toLocaleString()} deleted record${totalItems !== 1 ? "s" : ""} found`}
          </span>
          {totalPages > 1 && (
            <span style={{ fontSize: 12, color: "#9ea3bb" }}>
              Page {page + 1} of {totalPages}
            </span>
          )}
        </div>

        {/* ── Bulk action bar ── */}
        {selected.size > 0 && (
          <div
            className="d-flex align-items-center gap-3 mb-2 px-3 py-2"
            style={{ background: "#fff3cd", borderRadius: 10, border: "1px solid #ffc10740" }}
          >
            <span style={{ fontSize: 13, fontWeight: 600, color: "#856404" }}>
              {selected.size} entr{selected.size === 1 ? "y" : "ies"} selected
            </span>
            <button
              className="btn btn-sm"
              style={{ borderRadius: 8, fontSize: 12, background: "#dc3545", color: "#fff", fontWeight: 600 }}
              onClick={handleBulkDelete}
              disabled={deleting}
            >
              {deleting
                ? <><span className="spinner-border spinner-border-sm me-1" />Deleting…</>
                : <><i className="bi bi-trash3 me-1" />Delete Selected</>}
            </button>
            <button
              className="btn btn-sm btn-outline-secondary"
              style={{ borderRadius: 8, fontSize: 12 }}
              onClick={() => setSelected(new Set())}
            >
              Clear
            </button>
          </div>
        )}

        {/* ── Table ── */}
        <div
          className="card border-0"
          style={{ borderRadius: 12, boxShadow: "0 2px 12px rgba(8,2,53,0.07)", overflow: "hidden" }}
        >
          <div style={{ overflowX: "auto", overflowY: "auto", maxHeight: "60vh" }}>
            <table className="table table-hover mb-0" style={{ fontSize: 13.5 }}>
              <thead style={{ background: "#f8f9ff", position: "sticky", top: 0, zIndex: 2 }}>
                <tr>
                  <th style={{ ...thStyle, cursor: "default", width: 40 }}>
                    <input
                      type="checkbox"
                      checked={allOnPageSelected}
                      ref={(el) => { if (el) el.indeterminate = someOnPageSelected && !allOnPageSelected; }}
                      onChange={toggleAll}
                      style={{ cursor: "pointer", width: 15, height: 15 }}
                    />
                  </th>
                  <th style={thStyle} onClick={() => handleSort("id")}>Sr.no<SortArrow field="id" /></th>
                  <th style={thStyle} onClick={() => handleSort("entityType")}>Module<SortArrow field="entityType" /></th>
                  <th style={thStyle} onClick={() => handleSort("entityId")}>Record ID<SortArrow field="entityId" /></th>
                  <th style={thStyle} onClick={() => handleSort("entityName")}>Record Name<SortArrow field="entityName" /></th>
                  <th style={thStyle} onClick={() => handleSort("deletedBy")}>Deleted By<SortArrow field="deletedBy" /></th>
                  <th style={thStyle} onClick={() => handleSort("deletedAt")}>Deleted At<SortArrow field="deletedAt" /></th>
                  <th style={{ ...thStyle, cursor: "default" }}>Notes</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={8} className="text-center py-5">
                      <div className="spinner-border" style={{ width: 40, height: 40, color: "#080235", borderWidth: 3 }} role="status" />
                      <div style={{ marginTop: 12, fontSize: 13, color: "#9ea3bb" }}>Loading…</div>
                    </td>
                  </tr>
                )}
                {!loading && entries.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center py-5">
                      <div style={{ color: "#9ea3bb" }}>
                        <i className="bi bi-inbox" style={{ fontSize: 32, display: "block", marginBottom: 8 }} />
                        No deleted records found
                      </div>
                    </td>
                  </tr>
                )}
                {!loading && entries.map((entry, idx) => {
                  const isSelected = selected.has(entry.id);
                  return (
                  <tr
                    key={entry.id}
                    style={{ verticalAlign: "middle", background: isSelected ? "#f0f1ff" : undefined, cursor: "pointer" }}
                    onClick={() => toggleRow(entry.id)}
                  >
                    <td style={tdStyle} onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleRow(entry.id)}
                        style={{ cursor: "pointer", width: 15, height: 15 }}
                      />
                    </td>
                    <td style={tdStyle} className="text-muted">{page * pageSize + idx + 1}</td>
                    <td style={tdStyle}>
                      <span
                        style={{
                          display: "inline-block",
                          padding: "3px 10px",
                          borderRadius: 20,
                          fontSize: 11.5,
                          fontWeight: 600,
                          background: typeColor(entry.entityType) + "18",
                          color: typeColor(entry.entityType),
                          border: `1px solid ${typeColor(entry.entityType)}30`,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {entry.entityType || "—"}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <code style={{ fontSize: 12, color: "#5b5fc7", background: "#f0f1ff", padding: "2px 6px", borderRadius: 4 }}>
                        {entry.entityId || "—"}
                      </code>
                    </td>
                    <td style={tdStyle}>
                      {entry.entityName
                        ? <span className="fw-semibold">{entry.entityName}</span>
                        : <span className="text-muted">—</span>}
                    </td>
                    <td style={tdStyle}>
                      <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span
                          style={{
                            width: 28, height: 28, borderRadius: "50%",
                            background: "#e8eaf0", display: "inline-flex",
                            alignItems: "center", justifyContent: "center",
                            fontSize: 11, fontWeight: 700, color: "#374151",
                            flexShrink: 0,
                          }}
                        >
                          {(entry.deletedBy || "?").charAt(0).toUpperCase()}
                        </span>
                        <span>{entry.deletedBy || "—"}</span>
                      </span>
                    </td>
                    <td style={tdStyle} className="text-muted">{fmt(entry.deletedAt)}</td>
                    <td style={tdStyle} className="text-muted">
                      {entry.notes
                        ? <span title={entry.notes}>{entry.notes.length > 40 ? entry.notes.slice(0, 40) + "…" : entry.notes}</span>
                        : "—"}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* ── Pagination ── */}
          {totalPages > 1 && (
            <div
              className="d-flex align-items-center justify-content-between flex-wrap gap-2 px-4 py-3"
              style={{ borderTop: "1px solid #f0f1f5" }}
            >
              {/* Left: record count */}
              <span style={{ fontSize: 12, color: "#9ea3bb", minWidth: 120 }}>
                Page <strong style={{ color: "#374151" }}>{page + 1}</strong> of <strong style={{ color: "#374151" }}>{totalPages}</strong>
              </span>

              {/* Center: page buttons */}
              <div className="d-flex align-items-center gap-1">
                {/* First + Prev */}
                <button
                  style={pgBtn(false, page === 0)}
                  disabled={page === 0}
                  onClick={() => setPage(0)}
                  title="First page"
                >«</button>
                <button
                  style={pgBtn(false, page === 0)}
                  disabled={page === 0}
                  onClick={() => setPage((p) => p - 1)}
                >‹ Prev</button>

                {/* Numbered pages */}
                {(() => {
                  const range = [];
                  let start = Math.max(0, page - 2);
                  let end   = Math.min(totalPages - 1, start + 4);
                  if (end - start < 4) start = Math.max(0, end - 4);

                  if (start > 0) {
                    range.push(<button key={0} style={pgBtn(false)} onClick={() => setPage(0)}>1</button>);
                    if (start > 1) range.push(<span key="e1" style={ellipsis}>…</span>);
                  }
                  for (let i = start; i <= end; i++) {
                    range.push(
                      <button key={i} style={pgBtn(i === page)} onClick={() => setPage(i)}>
                        {i + 1}
                      </button>
                    );
                  }
                  if (end < totalPages - 1) {
                    if (end < totalPages - 2) range.push(<span key="e2" style={ellipsis}>…</span>);
                    range.push(<button key={totalPages - 1} style={pgBtn(false)} onClick={() => setPage(totalPages - 1)}>{totalPages}</button>);
                  }
                  return range;
                })()}

                {/* Next + Last */}
                <button
                  style={pgBtn(false, page >= totalPages - 1)}
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage((p) => p + 1)}
                >Next ›</button>
                <button
                  style={pgBtn(false, page >= totalPages - 1)}
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage(totalPages - 1)}
                  title="Last page"
                >»</button>
              </div>

              {/* Right: jump to page */}
              <div className="d-flex align-items-center gap-2" style={{ minWidth: 120, justifyContent: "flex-end" }}>
                <span style={{ fontSize: 12, color: "#9ea3bb", whiteSpace: "nowrap" }}>Go to</span>
                <input
                  type="number"
                  min={1}
                  max={totalPages}
                  defaultValue={page + 1}
                  key={page}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      const v = parseInt(e.target.value, 10);
                      if (v >= 1 && v <= totalPages) setPage(v - 1);
                    }
                  }}
                  style={{
                    width: 52, height: 30, borderRadius: 8, border: "1px solid #dde1ea",
                    textAlign: "center", fontSize: 12, outline: "none", color: "#374151",
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Confirm Delete Modal ── */}
      {showConfirm && (
        <div
          style={{
            position: "fixed", inset: 0, zIndex: 2000,
            background: "rgba(8,2,53,0.45)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
          onClick={() => setShowConfirm(false)}
        >
          <div
            style={{
              background: "#fff", borderRadius: 16, padding: "32px 28px",
              width: 420, maxWidth: "90vw",
              boxShadow: "0 20px 60px rgba(8,2,53,0.18)",
              animation: "modalIn 0.18s ease",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Icon */}
            <div style={{ textAlign: "center", marginBottom: 18 }}>
              <div style={{
                width: 60, height: 60, borderRadius: "50%",
                background: "#fff0f0", display: "inline-flex",
                alignItems: "center", justifyContent: "center",
              }}>
                <i className="bi bi-trash3-fill" style={{ fontSize: 26, color: "#dc3545" }} />
              </div>
            </div>

            {/* Title */}
            <h5 className="text-center fw-bold mb-1" style={{ color: "#080235" }}>
              Delete {selected.size} Log {selected.size === 1 ? "Entry" : "Entries"}?
            </h5>
            <p className="text-center text-muted mb-4" style={{ fontSize: 13 }}>
              This will permanently remove the selected audit log{selected.size === 1 ? "" : "s"} from the system.
              <br />
              <strong style={{ color: "#dc3545" }}>This action cannot be undone.</strong>
            </p>

            {/* Buttons */}
            <div className="d-flex gap-2">
              <button
                className="btn w-50"
                style={{ borderRadius: 10, border: "1px solid #dde1ea", color: "#374151", fontWeight: 500, fontSize: 14 }}
                onClick={() => setShowConfirm(false)}
              >
                Cancel
              </button>
              <button
                className="btn w-50"
                style={{ borderRadius: 10, background: "#dc3545", color: "#fff", fontWeight: 600, fontSize: 14, border: "none" }}
                onClick={confirmDelete}
              >
                <i className="bi bi-trash3 me-1" />
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes modalIn { from { opacity:0; transform:scale(0.93) } to { opacity:1; transform:scale(1) } }`}</style>
    </div>
  );
}

const thStyle = {
  padding: "12px 14px",
  fontWeight: 600,
  fontSize: 12,
  color: "#6b7394",
  textTransform: "uppercase",
  letterSpacing: "0.4px",
  cursor: "pointer",
  whiteSpace: "nowrap",
  borderBottom: "1px solid #eef0f4",
};

const tdStyle = {
  padding: "11px 14px",
  borderBottom: "1px solid #f5f6fa",
};

const pgBtn = (active = false, disabled = false) => ({
  minWidth: 34,
  height: 32,
  padding: "0 10px",
  borderRadius: 8,
  border: active ? "none" : "1px solid #dde1ea",
  background: active ? "#080235" : disabled ? "#f5f6fa" : "#fff",
  color: active ? "#fff" : disabled ? "#c0c4d0" : "#374151",
  fontSize: 12,
  fontWeight: active ? 600 : 400,
  cursor: disabled ? "not-allowed" : "pointer",
  transition: "all 0.15s",
  whiteSpace: "nowrap",
  boxShadow: active ? "0 2px 8px rgba(8,2,53,0.18)" : "none",
});

const ellipsis = {
  display: "inline-flex", alignItems: "center", justifyContent: "center",
  width: 28, height: 32, fontSize: 13, color: "#9ea3bb", userSelect: "none",
};

